/**
 * ImprimirPericial.jsx
 *
 * Sección "Imprimir → Informe pericial" para admin.
 * El admin selecciona una OT, redacta el texto y se genera un PDF que contiene:
 *   • Portada del informe pericial (con datos de empresa y texto del admin).
 *   • Anexo: informe técnico de la OT sin precios + reportaje fotográfico.
 *
 * Imprime con react-to-print → el navegador genera el PDF (Save as PDF).
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Printer, FileText, Search, Loader2, Wand2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import OrdenPDF from '@/components/OrdenPDF';
import InformePericialPDF from '@/components/InformePericialPDF';
import CondicionesGeneralesPDF from '@/components/CondicionesGeneralesPDF';

const PLANTILLA_DEFECTO = `Tras la inspección técnica realizada al dispositivo arriba identificado, se ha podido constatar lo siguiente:

1. Estado de recepción.
[Describir estado físico, daños visibles, manipulaciones previas detectadas]

2. Diagnóstico técnico.
[Describir el fallo identificado, pruebas realizadas y resultados]

3. Causa probable.
[Detallar la causa más probable del fallo: uso, golpe, líquido, defecto de fabricación, etc.]

4. Conclusión.
[Resumen final del análisis técnico realizado]

Este informe se emite a petición del cliente / aseguradora y se basa exclusivamente en los datos técnicos observados al examinar el equipo.`;

export default function ImprimirPericial() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [ordenes, setOrdenes] = useState([]);
  const [ordenSel, setOrdenSel] = useState(null);
  const [clienteSel, setClienteSel] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const [titulo, setTitulo] = useState('Informe técnico');
  const [cuerpo, setCuerpo] = useState(PLANTILLA_DEFECTO);
  const [perito, setPerito] = useState('');

  // Refs para impresión
  const fullDocRef = useRef(null);

  // Espera a que todas las <img> dentro del nodo terminen de cargar antes de
  // disparar window.print(). Sin esto el PDF aparece con huecos negros / errores
  // porque Chrome dispara la captura antes de que Cloudinary entregue las imágenes.
  const waitForImages = (node, timeoutMs = 12000) => {
    if (!node) return Promise.resolve();
    const imgs = Array.from(node.querySelectorAll('img'));
    if (imgs.length === 0) return Promise.resolve();
    return new Promise((resolve) => {
      let resolved = false;
      const done = () => { if (!resolved) { resolved = true; resolve(); } };
      const timer = setTimeout(done, timeoutMs);
      Promise.all(
        imgs.map((img) => new Promise((res) => {
          if (img.complete) return res();
          const onLoad = () => { img.removeEventListener('load', onLoad); img.removeEventListener('error', onErr); res(); };
          const onErr  = () => { img.removeEventListener('load', onLoad); img.removeEventListener('error', onErr); res(); };
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onErr);
        })),
      ).then(() => { clearTimeout(timer); done(); });
    });
  };

  const doPrint = useReactToPrint({
    contentRef: fullDocRef,
    documentTitle: ordenSel?.numero_orden
      ? `informe-tecnico-${ordenSel.numero_orden}`
      : 'informe-tecnico',
  });

  const handlePrint = async () => {
    if (!ordenSel) return;
    const t = toast.loading('Cargando imágenes para el informe…');
    try {
      await waitForImages(fullDocRef.current);
    } finally {
      toast.dismiss(t);
    }
    doPrint();
  };

  useEffect(() => {
    if (user?.nombre) setPerito(`${user.nombre}${user.apellidos ? ' ' + user.apellidos : ''}`);
  }, [user]);

  // Buscar órdenes por número, cliente, IMEI o modelo
  const handleSearch = async () => {
    if (search.trim().length < 2) {
      toast.error('Introduce al menos 2 caracteres');
      return;
    }
    setSearching(true);
    try {
      const { data } = await API.get(`/ordenes?search=${encodeURIComponent(search.trim())}&limit=20`);
      const items = Array.isArray(data) ? data : (data.items || data.ordenes || []);
      setOrdenes(items);
      if (items.length === 0) toast.info('No se encontraron órdenes');
    } catch (e) {
      toast.error('Error buscando órdenes');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectOrden = async (o) => {
    setLoadingDetalle(true);
    try {
      // Cargar detalle completo (cliente, dispositivo, fotos)
      const { data } = await API.get(`/ordenes/${o.id}`);
      setOrdenSel(data.orden || data);
      // Cargar cliente
      const cliId = (data.orden || data).cliente_id;
      if (cliId) {
        try {
          const { data: cli } = await API.get(`/clientes/${cliId}`);
          setClienteSel(cli);
        } catch (_) {}
      }
      setOrdenes([]);
      setSearch('');
    } catch (e) {
      toast.error('Error cargando OT');
    } finally {
      setLoadingDetalle(false);
    }
  };

  // Auto-carga cuando se llega con ?orden_id=XXX desde el detalle de una OT
  useEffect(() => {
    const ordenIdQS = searchParams.get('orden_id');
    if (!ordenIdQS || ordenSel?.id === ordenIdQS) return;
    handleSelectOrden({ id: ordenIdQS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleClear = () => {
    setOrdenSel(null);
    setClienteSel(null);
    setSearch('');
    setOrdenes([]);
    if (searchParams.get('orden_id')) {
      searchParams.delete('orden_id');
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="imprimir-pericial-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Printer className="w-6 h-6 text-primary" />
          Imprimir · Informe técnico
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Genera un informe técnico sobre cualquier orden o dispositivo del taller.
          El documento se emite con la imagen corporativa, sin precios y se adjunta al informe técnico de la OT.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selector de OT */}
        <Card data-testid="card-selector-ot">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4 text-primary" />
              1. Selecciona la orden / dispositivo
            </CardTitle>
            <CardDescription>Busca por número de OT, cliente, IMEI o modelo.</CardDescription>
          </CardHeader>
          <CardContent>
            {!ordenSel ? (
              <>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ej. OT-2026-001 / 35XXXXXXX / Galaxy S23"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="input-buscar-ot"
                  />
                  <Button onClick={handleSearch} disabled={searching} data-testid="btn-buscar-ot">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {ordenes.length > 0 && (
                  <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto">
                    {ordenes.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => handleSelectOrden(o)}
                        data-testid={`select-ot-${o.numero_orden}`}
                        className="w-full text-left rounded-md border border-border bg-muted/20 hover:bg-muted/40 px-3 py-2 transition-colors"
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-mono font-semibold">{o.numero_orden}</span>
                          <span className="text-xs text-muted-foreground capitalize">{o.estado}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {o.cliente_nombre || '—'} · {o.dispositivo?.modelo || '—'}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-mono font-semibold">{ordenSel.numero_orden}</p>
                  <Button size="sm" variant="ghost" onClick={handleClear} data-testid="btn-cambiar-ot">
                    Cambiar
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Cliente: <span className="text-foreground">{clienteSel?.nombre || ordenSel.cliente_nombre || '—'}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Dispositivo: <span className="text-foreground">{ordenSel.dispositivo?.modelo || '—'}</span>
                  {ordenSel.dispositivo?.imei && <> · IMEI <span className="font-mono text-foreground">{ordenSel.dispositivo.imei}</span></>}
                </p>
                <p className="text-xs text-muted-foreground">
                  Estado: <span className="text-foreground capitalize">{ordenSel.estado || '—'}</span>
                  {' '}· Técnico: <span className="text-foreground">{ordenSel.tecnico_nombre || ordenSel.tecnico_asignado || '—'}</span>
                </p>
              </div>
            )}
            {loadingDetalle && (
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Cargando detalle…
              </div>
            )}
          </CardContent>
        </Card>

        {/* Editor del informe */}
        <Card data-testid="card-editor-pericial">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              2. Redacta el informe técnico
            </CardTitle>
            <CardDescription>
              Texto que aparecerá en la portada del documento. El detalle técnico de la OT y las fotos se adjuntan automáticamente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Título del informe</Label>
              <Input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Informe técnico"
                data-testid="input-titulo-pericial"
              />
            </div>
            <div>
              <Label className="text-xs">Perito / técnico emisor</Label>
              <Input
                value={perito}
                onChange={(e) => setPerito(e.target.value)}
                placeholder="Tu nombre"
                data-testid="input-perito"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Cuerpo del informe</Label>
                <Button
                  variant="ghost" size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => setCuerpo(PLANTILLA_DEFECTO)}
                  data-testid="btn-restaurar-plantilla"
                >
                  <Wand2 className="w-3 h-3 mr-1" /> Plantilla
                </Button>
              </div>
              <Textarea
                rows={12}
                value={cuerpo}
                onChange={(e) => setCuerpo(e.target.value)}
                placeholder="Análisis técnico del dispositivo…"
                data-testid="textarea-cuerpo-pericial"
                className="font-mono text-xs leading-relaxed"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {cuerpo.length} caracteres · soporta saltos de línea y formateo libre.
              </p>
            </div>
            <Button
              onClick={handlePrint}
              disabled={!ordenSel || !titulo.trim() || !cuerpo.trim()}
              className="w-full"
              data-testid="btn-generar-pericial-pdf"
            >
              <Printer className="w-4 h-4 mr-2" />
              Generar PDF / Imprimir
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Render del PDF — se mantiene oculto hasta que el usuario imprime.
          Orden: 1) Informe técnico (ficha de servicio sin precios)
                 2) Informe pericial (cover + cuerpo redactado por el admin)
                 3) Anexo fotográfico (las fotos justo antes del cierre) */}
      <div
        id="pericial-print-area"
        style={{ position: 'absolute', left: '-10000px', top: 0 }}
        aria-hidden="true"
      >
        <div ref={fullDocRef}>
          {ordenSel && (
            <>
              {/* 1. Ficha técnica de la OT */}
              <OrdenPDF
                orden={ordenSel}
                cliente={clienteSel}
                materiales={ordenSel?.materiales || []}
                mode="no_prices"
                modoB2B={(clienteSel?.tipo_cliente || '').toLowerCase() === 'empresa'}
                includeFotos={false}
                onlyMain={true}
                skipCondiciones={true}
              />
              {/* 2. Informe pericial (texto redactado por el admin) */}
              <InformePericialPDF
                orden={ordenSel}
                cliente={clienteSel}
                titulo={titulo}
                cuerpo={cuerpo}
                perito={perito}
                fechaEmision={new Date()}
              />
              {/* 3. Anexo fotográfico — justo antes del cierre */}
              <OrdenPDF
                orden={ordenSel}
                cliente={clienteSel}
                materiales={ordenSel?.materiales || []}
                mode="no_prices"
                modoB2B={(clienteSel?.tipo_cliente || '').toLowerCase() === 'empresa'}
                includeFotos={true}
                onlyFotos={true}
              />
              {/* 4. Condiciones generales del servicio técnico (anexo legal final) */}
              <CondicionesGeneralesPDF
                orden={ordenSel}
                empresa={null}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
